/**
 * Popover component built on Carbon Popover
 */

import * as React from 'react';
import { Popover as CarbonPopover, PopoverContent as CarbonPopoverContent } from '@carbon/react';

import { cn } from '@sphere/shared/util';

const alignMap = {
  start: 'bottom-start',
  center: 'bottom',
  end: 'bottom-end',
  top: 'top',
  'top-start': 'top-start',
  'top-end': 'top-end',
  bottom: 'bottom',
  'bottom-start': 'bottom-start',
  'bottom-end': 'bottom-end',
  left: 'left',
  'left-start': 'left-start',
  'left-end': 'left-end',
  right: 'right',
  'right-start': 'right-start',
  'right-end': 'right-end',
} as const;

interface PopoverContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null);

const Popover = ({
  open,
  onOpenChange,
  onRequestClose,
  children,
  ...props
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onRequestClose?: () => void;
  children: React.ReactNode;
}) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const handleClose = () => {
    if (isControlled) {
      onOpenChange?.(false);
    } else {
      setInternalOpen(false);
    }
    onRequestClose?.();
  };

  const setOpen = (value: boolean) => {
    if (isControlled) {
      onOpenChange?.(value);
    } else {
      setInternalOpen(value);
    }
  };

  const childArray = React.Children.toArray(children);
  let trigger: React.ReactNode = null;
  let content: React.ReactNode = null;
  let align: keyof typeof alignMap = 'center';

  for (const child of childArray) {
    if (React.isValidElement(child)) {
      const type = child.type as React.ComponentType & { displayName?: string };
      if (type.displayName === 'PopoverTrigger') {
        trigger = child;
      } else if (type.displayName === 'PopoverContent') {
        content = child;
        align = (child.props as { align?: keyof typeof alignMap }).align ?? 'center';
      }
    }
  }

  return (
    <PopoverContext.Provider value={{ open: isOpen, setOpen }}>
      <CarbonPopover
        open={isOpen}
        onRequestClose={handleClose}
        align={alignMap[align] ?? 'bottom'}
        caret={false}
        {...props}
      >
        {trigger}
        {content}
      </CarbonPopover>
    </PopoverContext.Provider>
  );
};

const PopoverTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<'button'> & { asChild?: boolean }
>(({ asChild, children, onClick, ...props }, ref) => {
  const context = React.useContext(PopoverContext);
  if (!context) throw new Error('PopoverTrigger must be used within Popover');

  const { setOpen } = context;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    setOpen(true);
  };

  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void; ref?: React.Ref<unknown> }>;
    const childRef = 'ref' in child.props ? child.props.ref : undefined;
    return React.cloneElement(child, {
      onClick: (e: React.MouseEvent) => {
        child.props.onClick?.(e);
        setOpen(true);
      },
      ref: childRef ?? ref,
    } as Record<string, unknown>);
  }

  return (
    <button ref={ref} onClick={handleClick} type="button" {...props}>
      {children}
    </button>
  );
});
PopoverTrigger.displayName = 'PopoverTrigger';

const PopoverContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { align?: keyof typeof alignMap; sideOffset?: number }
>(({ className, align, sideOffset, ...props }, ref) => (
  <CarbonPopoverContent
    ref={ref}
    className={cn(
      'z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none',
      className
    )}
    {...props}
  />
));
PopoverContent.displayName = 'PopoverContent';

export { Popover, PopoverTrigger, PopoverContent };
